<!-- THE MACHINE — and the machine is the navigation.
     There is no tab bar. A tab bar is generic app furniture and it would fight
     the idea that this is a physical object on a desk. The parts do the work:
     the paper tray starts a fax, the output tray opens the outbox, SETUP opens
     settings.

     EVERY PART IS A REAL BUTTON with a real accessible name. A drawing with tap
     zones has no name, no focus ring and no target bigger than its artwork —
     it deletes the accessibility layer, which is what the persona pass found.
     The drawing is the skin; the buttons are the control.

     And every part is SILKSCREENED, because every fax machine ever built had
     printed labels beside its parts. It is period-accurate, it fixes the
     in/out confusion that almost every first-time tester hit, and the machine
     never breaks character — the factory printed it, not the machine. -->
<template>
  <IonPage>
    <AppHeader title="Just the Fax" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Just the Fax" />

      <!-- LARGE CONTROLS: the same three destinations as plain, high-contrast
           rows at full Dynamic Type. The machine is a picture, and a picture is
           not an interface for everyone. -->
      <div v-if="m.largeControls" class="pad plainlist stack" style="margin-top: var(--sp-4)">
        <!-- LARGE CONTROLS used to drop the LCD along with the drawing, so the
             one person most likely to be confused by a machine that has stopped
             working was also the only person the machine never told. -->
        <p class="silkscreen" style="font-size: var(--text-md); margin: 0">{{ status }}</p>

        <button v-if="m.jammed" type="button" class="part" @click="unjam">
          CLEAR THE JAM
          <div class="silkscreen">Nothing will print until it is cleared</div>
        </button>

        <button v-else type="button" class="part" @click="go('/tabs/machine/compose')">
          NEW FAX
          <div class="silkscreen">Put a photo or a note on a page</div>
        </button>
        <button type="button" class="part" @click="go('/tabs/outbox')">
          OUTBOX ({{ faxs.length }})
          <div class="silkscreen">Everything this machine has made</div>
        </button>
        <button type="button" class="part" @click="go('/tabs/settings')">
          SETUP
          <div class="silkscreen">The machine's name, sound and servicing</div>
        </button>
      </div>

      <div v-else class="machine">
        <!-- control panel -->
        <div class="panel">
          <div class="lcd" role="status">{{ status }}</div>
          <span class="lamp" :class="{ 'is-on': !m.jammed }">
            <i aria-hidden="true" /><span class="silkscreen">Ready</span>
          </span>
          <span class="lamp fault" :class="{ 'is-on': m.jammed }">
            <i aria-hidden="true" /><span class="silkscreen">Jam</span>
          </span>
          <button type="button" class="part" style="width:auto" @click="go('/tabs/settings')">
            <span class="silkscreen">Setup</span>
          </button>
        </div>

        <!-- The unit's own serial and page count, silkscreened on the case.
             The counter only ever goes up, which is what gives the prop a life
             of its own. -->
        <p class="silkscreen center" style="margin: 0">
          MODEL JTF-1 &nbsp;·&nbsp; PAGES {{ String(m.pageCount || 0).padStart(5, '0') }}
          <template v-if="tonerLow"> &nbsp;·&nbsp; TONER LOW</template>
        </p>

        <!-- OUTPUT TRAY, at the top where a fax machine's is, with the last
             page still sitting in it. That resting state restores context for
             someone who comes back a month later AND labels the tray. -->
        <button
          type="button"
          class="part tray tray-out"
          :aria-label="`Output tray. ${faxs.length} faxes. Opens the outbox.`"
          @click="go('/tabs/outbox')"
        >
          <span class="silkscreen">Out tray · printed faxes</span>
          <span class="tray-title">Outbox · {{ faxs.length }}</span>
          <div v-if="latestThumb" class="sheet" style="margin-top: var(--sp-3)">
            <img class="thumb" :src="latestThumb" alt="" width="540" height="714">
          </div>
          <!-- ON `faxs.length`, NOT ON THE THUMBNAIL. Nothing had ever written
               a thumbnail, so this branch was the only one that ever ran: the
               tray read OUTBOX EMPTY. NOTHING HAS LEFT THIS BUILDING directly
               underneath OUTBOX · 1. A machine that contradicts itself on its
               own front panel is not a prop, it is a bug. -->
          <p v-else-if="!faxs.length" class="silkscreen says" style="margin: var(--sp-3) 0 0">
            {{ say(
              'OUTBOX EMPTY. NOTHING HAS LEFT THIS BUILDING.',
              'Nothing here yet. Every fax you make lands in this tray.',
            ) }}
          </p>
          <p v-else class="silkscreen says" style="margin: var(--sp-3) 0 0">
            {{ faxs.length }} PAGE{{ faxs.length === 1 ? '' : 'S' }} IN THE TRAY.
          </p>
        </button>

        <!-- TONER. The panel said TONER LOW and then offered nothing to do
             about it: the cure was a button on the SETUP screen, two taps away,
             which nobody who has not already read SETUP knows exists. A fault a
             machine reports is a fault the machine has to let you fix, at the
             place it reports it. -->
        <button
          v-if="tonerLow && !m.jammed"
          type="button"
          class="part"
          @click="shake"
        >
          <span class="silkscreen">Toner cartridge · {{ Math.round(m.toner) }}%</span>
          <span class="tray-title">Shake the cartridge</span>
        </button>

        <!-- PAPER TRAY, at the bottom, which is both where a fax machine's is
             and the only part of a 6.7" screen a thumb reaches.

             WHILE IT IS JAMMED, THE PRIMARY ACTION IS THE REPAIR. The jam used
             to be pure decoration — two lamps and a line on the LCD — while
             LOAD ORIGINAL carried on printing perfectly, and the SETUP screen
             stated in plain words that "nothing will print until it is
             cleared", which was simply untrue. Either the fault means something
             or it should not be shown. It means something now, and the way out
             of it is one tap, in the thumb zone, on the part that is stuck. -->
        <button
          v-if="m.jammed"
          type="button"
          class="part tray tray-in"
          aria-label="Paper jam. Clear the jam to start faxing again."
          @click="unjam"
        >
          <span class="silkscreen">Paper path · sheet caught in the rollers</span>
          <span class="tray-title">{{ say('PAPER JAM', 'Paper jam') }}</span>
          <span class="press">Clear the jam ›</span>
        </button>

        <button
          v-else
          type="button"
          class="part tray tray-in"
          aria-label="Paper tray. Load an original and start a new fax."
          @click="go('/tabs/machine/compose')"
        >
          <span class="silkscreen">Paper tray · load original face down</span>
          <span class="tray-title">Load Original</span>
          <span class="press">Start a fax ›</span>
        </button>

        <!-- IT SAID "SEE SETUP" AND WAS NOT TAPPABLE. A machine that names a
             problem and then leaves you to find the cure yourself has invented
             a dead end out of a one-line fix. The name is also the joke — it is
             printed across the top of every page this thing produces — so
             getting to it has to be the easiest thing on the screen. -->
        <button
          v-if="needsName"
          type="button"
          class="part center"
          style="background: none; box-shadow: none; padding: var(--sp-2)"
          @click="go('/tabs/settings')"
        >
          <span class="silkscreen says">
            {{ say(
              'THIS MACHINE HAS NO NAME. NAME IT ›',
              'This machine has no name. Name it ›',
            ) }}
          </span>
        </button>
      </div>

      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { AppHeader, AppLargeTitle } from '../core/ui';
import { haptic } from '../core/util.js';
import { announce } from '../core/a11y.js';
import { faxs, storeReady } from '../store.js';
import {
  machine, ensureMachine, needsName, tonerLow, clearJam, shakeCartridge,
} from '../fax/machine.js';
import { ensureTestPage } from '../fax/firstrun.js';
import { say } from '../fax/voice.js';

const router = useRouter();
const m = machine;

// THE NEWEST, NOT THE FIRST IN THE ARRAY. `addFax` prepends, so within one
// session index 0 is the newest — but after a reload the store hydrates from
// IndexedDB in key order, so index 0 is the OLDEST, and the out tray showed
// the first fax the machine ever printed as the page lying in it.
const latest = computed(() => [...faxs.value].sort(
  (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
      || Number(b.id) - Number(a.id),
)[0] ?? null);
// Demo fixtures store the string 'Sample thumb' in this field, and a broken
// <img> in the out tray is worse than an empty one.
const latestThumb = computed(() => {
  const t = latest.value?.thumb;
  return typeof t === 'string' && t.startsWith('data:image') ? t : '';
});

// It used to point at SETUP, which was both a detour and a lie — SETUP could
// clear the jam but the jam never stopped anything, so following the
// instruction taught you the fault was cosmetic. The cure is now the paper tray
// directly below this line.
const status = computed(() => {
  if (m.value.jammed) return say('PAPER JAM — CLEAR IT BELOW', 'Paper jam. Clear it below.');
  if (tonerLow.value) return say('TONER LOW', 'Toner is low.');
  return say('READY', 'Ready.');
});

/** A jam clears with a thump, so it gets the bigger haptic. */
function unjam() {
  haptic('pop');
  clearJam();
  announce(say('JAM CLEARED. READY.', 'Jam cleared. The machine is ready.'));
}

function shake() {
  haptic('pop');
  shakeCartridge();
  announce(`Toner now ${Math.round(m.value.toner)} per cent.`);
}

// A part of a machine makes a noise when you press it. There is nothing to
// wait for here — the push transition is the visible feedback — so the haptic
// is a `tick` and nothing else: proportion, not celebration.
const go = (path) => { haptic('tick'); return router.push(path); };

onMounted(async () => {
  await storeReady;
  await ensureMachine();
  // The machine prints its own test page the first time it is switched on, so
  // the first thing anybody sees in the out tray is a fax rather than a
  // sentence about there not being one. Not awaited by anything on screen: the
  // page renders empty for a beat and then has a fax in it, which is the
  // correct order of events for a machine that has just been plugged in.
  await ensureTestPage();
});
</script>
